import { useState, useEffect } from 'react';
import { AlertTriangle, User, MapPin, Route, Loader2, XCircle, CheckCircle } from 'lucide-react';
import unifiedApi from '../services/unifiedApi';
import logger from '@/utils/logger';

const BulkDeleteImpactModal = ({ students, onConfirm, onCancel }) => {
  const [impacts, setImpacts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadImpacts();
  }, []);

  const loadImpacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await unifiedApi.students.previewBulkDelete(students.map((s) => s.id));
      if (!response.success) throw new Error(response.error || 'Erreur chargement impacts');
      setImpacts(response.impacts);
    } catch (err) {
      logger.error('Erreur chargement impacts bulk:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const hasAnyImpact =
    impacts &&
    (impacts.parentsToDelete?.length > 0 ||
      impacts.parentsToDeactivate?.length > 0 ||
      impacts.stopsToDelete?.length > 0 ||
      impacts.stopsToDeactivate?.length > 0 ||
      impacts.affectedRoutesCount > 0);

  const renderNameList = (items) =>
    items.length > 0 && (
      <span className="block mt-0.5 ml-6 text-xs opacity-80">{items.map((i) => i.name).join(', ')}</span>
    );

  const renderImpacts = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="ml-2 text-sm text-gray-600">Analyse des impacts en cours...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center py-8 text-red-600">
          <XCircle className="w-5 h-5 mr-2" />
          <span className="text-sm">Erreur : {error}</span>
        </div>
      );
    }

    if (!impacts) return null;

    const studentNames = students.map((s) =>
      `${s.first_name || s.firstName || ''} ${s.last_name || s.lastName || ''}`.trim(),
    );

    return (
      <div className="bg-red-50 rounded-lg p-4 mb-6 space-y-3">
        <p className="text-sm font-medium text-red-800 mb-2">Conséquences de la suppression :</p>

        {/* Élèves */}
        <div className="flex items-start text-sm text-red-700">
          <User className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
          <span>
            <strong>Élèves :</strong> {students.length} élève(s) supprimé(e)s définitivement
            {renderNameList(studentNames.map((name) => ({ name })))}
          </span>
        </div>

        {/* Parents à supprimer */}
        {impacts.parentsToDelete?.length > 0 && (
          <div className="flex items-start text-sm text-red-700">
            <User className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Parents supprimés</strong> (dernier enfant) : {impacts.parentsToDelete.length}
              {renderNameList(impacts.parentsToDelete)}
            </span>
          </div>
        )}

        {/* Parents à désactiver */}
        {impacts.parentsToDeactivate?.length > 0 && (
          <div className="flex items-start text-sm text-red-700">
            <User className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Parents désactivés</strong> (dernier enfant actif) : {impacts.parentsToDeactivate.length}
              {renderNameList(impacts.parentsToDeactivate)}
            </span>
          </div>
        )}

        {/* Arrêts à supprimer */}
        {impacts.stopsToDelete?.length > 0 && (
          <div className="flex items-start text-sm text-red-700">
            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Arrêts supprimés</strong> (dernier élève) : {impacts.stopsToDelete.length}
              {renderNameList(impacts.stopsToDelete)}
            </span>
          </div>
        )}

        {/* Arrêts à désactiver */}
        {impacts.stopsToDeactivate?.length > 0 && (
          <div className="flex items-start text-sm text-red-700">
            <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Arrêts désactivés</strong> (dernier élève actif) : {impacts.stopsToDeactivate.length}
              {renderNameList(impacts.stopsToDeactivate)}
            </span>
          </div>
        )}

        {/* Circuits */}
        {impacts.affectedRoutesCount > 0 && (
          <div className="flex items-start text-sm text-red-700">
            <Route className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Circuits mis à jour :</strong> {impacts.affectedRoutesCount}
              {impacts.routeNames?.length > 0 && (
                <span className="block mt-0.5 ml-6 text-xs opacity-80">{impacts.routeNames.join(', ')}</span>
              )}
            </span>
          </div>
        )}

        {!hasAnyImpact && (
          <div className="flex items-center text-sm text-gray-600">
            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
            <span>Aucun impact sur d'autres entités</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 border-l-4 border-red-500 animate-in fade-in zoom-in duration-200">
        <div className="flex items-start mb-4">
          <div className="p-2 bg-red-100 rounded-full mr-3">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Supprimer {students.length} élève(s)</h3>
            <p className="text-sm text-gray-600 mt-1">
              Vous êtes sur le point de supprimer définitivement {students.length} élève(s) sélectionné(s).
            </p>
          </div>
        </div>

        {renderImpacts()}

        <div className="flex space-x-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={loading || !!error}
            className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Chargement...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkDeleteImpactModal;
